// src/pages/shop/ProfilePage.js - COMPLETE REPLACEMENT

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  User, Camera, Save, MapPin, AlertCircle, Star, Package,
  Award, Briefcase, GraduationCap, Target, Phone, Mail,
  Globe, Plus, Trash2, DollarSign, Clock, BarChart2, Store
} from 'lucide-react';
import { Home, Search, MessageCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useActiveShop } from '../../contexts/ActiveShopContext';
import {
  doc,
  updateDoc,
  getDoc,
  serverTimestamp,
  addDoc,
  collection,
  query,
  where,
  getDocs,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../../firebase/config';
import { updateProfile, signOut } from 'firebase/auth';
import { useStripeAccountCheck } from '../../utils/useStripeAccountCheck';
import NotificationService from '../../services/NotificationService';
import StripeWizard from '../../components/Stripe/StripeWizard';
import AccountBalanceButton from '../../components/Billing/AccountBalanceButton';
import PaymentModal from '../../components/Billing/PaymentModal';

const BACKEND_URL = '/api';


const StripeStatusBanner = styled.div`
  background: ${props => {
    if (props.type === 'active') return 'linear-gradient(135deg, rgba(0, 200, 83, 0.1), rgba(0, 230, 118, 0.1))';
    if (props.type === 'pending') return 'linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(255, 213, 79, 0.1))';
    return 'linear-gradient(135deg, rgba(99, 91, 255, 0.1), rgba(79, 70, 229, 0.1))';
  }};
  border: 1px solid ${props => {
    if (props.type === 'active') return 'rgba(0, 200, 83, 0.3)';
    if (props.type === 'pending') return 'rgba(255, 193, 7, 0.3)';
    return 'rgba(99, 91, 255, 0.3)';
  }};
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .icon {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: ${props => {
      if (props.type === 'active') return 'rgba(0, 200, 83, 0.2)';
      if (props.type === 'pending') return 'rgba(255, 193, 7, 0.2)';
      return 'rgba(99, 91, 255, 0.2)';
    }};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => {
      if (props.type === 'active') return '#00C853';
      if (props.type === 'pending') return '#FFC107';
      return '#635BFF';
    }};
  }
  
  .content {
    flex: 1;
    
    h4 {
      margin: 0 0 0.25rem 0;
      color: white;
      font-size: 1rem;
    }
    
    p {
      margin: 0;
      color: rgba(255, 255, 255, 0.8);
      font-size: 0.9rem;
      line-height: 1.5;
    }
  }
  
  .action {
    button {
      background: ${props => {
        if (props.type === 'active') return '#00C853';
        if (props.type === 'pending') return '#FFC107';
        return '#635BFF';
      }};
      color: white;
      border: none;
      padding: 0.5rem 1rem;
      border-radius: 6px;
      font-weight: 600;
      cursor: pointer;
      font-size: 0.9rem;
      
      &:hover {
        opacity: 0.9;
      }
    }
  }
`;



const StatusBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: ${props => props.active ? '#00C853' : '#FFC107'};
  color: ${props => props.active ? 'white' : '#000'};
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  pointer-events: none;
  
  @media (max-width: 768px) {
    display: none;
  }
`;



const ProfileContent = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const ProfileGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  
  @media (min-width: 1024px) {
    grid-template-columns: 350px 1fr;
    gap: 2rem;
  }
`;

const ProfileSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ProfileCard = styled.div`
  background: rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(128, 0, 0, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(128, 0, 0, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  }
  
  .card-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    
    h2 {
      margin: 0;
      color: rgba(255, 255, 255, 0.9);
      font-size: 1.2rem;
    }
  }
`;


const ProfileHeader = styled.header`
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(128, 0, 0, 0.3);
  position: sticky;
  top: 0;
  z-index: 10;
  
  @media (min-width: 768px) {
    padding: 1.5rem 2rem;
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;

  h1 {
    font-size: 1.5rem;
    color: #FFFFFF;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    @media (max-width: 768px) {
      font-size: 1.3rem;
      gap: 0.5rem;
    }
    
    @media (min-width: 768px) {
      font-size: 1.8rem;
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

// UPDATE SaveButton for mobile
const SaveButton = styled.button`
  background: ${props => props.hasChanges ? 'linear-gradient(45deg, #800000, #4A0404)' : 'rgba(128, 0, 0, 0.3)'};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: ${props => props.hasChanges ? 'pointer' : 'not-allowed'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
  opacity: ${props => props.hasChanges ? 1 : 0.5};
  
  &:hover {
    ${props => props.hasChanges && 'transform: translateY(-2px); box-shadow: 0 4px 12px rgba(128, 0, 0, 0.3);'}
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
  
  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
  }
`;

// UPDATE AvatarSection to include Stripe button
const AvatarSection = styled.div`
  text-align: center;
  
  .avatar-container {
    position: relative;
    display: inline-block;
    margin-bottom: 1.5rem;
    
    .avatar {
      width: 150px;
      height: 150px;
      border-radius: 50%;
      background: rgba(128, 0, 0, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border: 4px solid rgba(128, 0, 0, 0.5);
      transition: all 0.3s ease;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      
      svg {
        color: rgba(255, 255, 255, 0.7);
      }
      
      &:hover {
        border-color: #800000;
        transform: scale(1.02);
      }
    }
    
    .avatar-upload {
      position: absolute;
      bottom: 0;
      right: 0;
      background: linear-gradient(45deg, #800000, #4A0404);
      border: 3px solid #0B0B3B;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        transform: scale(1.1);
        box-shadow: 0 4px 12px rgba(128, 0, 0, 0.4);
      }
      
      input {
        display: none;
      }
    }
  }
  
  .display-name {
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 0.5rem;
    color: #800000;
    
    @media (max-width: 768px) {
      font-size: 1.3rem;
    }
  }
  
  .professional-title {
    font-size: 1.1rem;
    opacity: 0.9;
    margin-bottom: 0.5rem;
    color: #FFFFFF;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
  
  .location {
    font-size: 0.9rem;
    opacity: 0.7;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 0.85rem;
    }
  }
  
  .verification-badges {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
  }
  
  .badge {
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    
    &.verified {
      background: rgba(76, 175, 80, 0.2);
      color: #4CAF50;
      border: 1px solid rgba(76, 175, 80, 0.3);
    }
    
    &.seller {
      background: rgba(33, 150, 243, 0.2);
      color: #2196F3;
      border: 1px solid rgba(33, 150, 243, 0.3);
    }
  }
  
  /* NEW: Stripe button section in avatar area */
  .stripe-section {
    margin: 1.5rem 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    
    @media (max-width: 768px) {
      margin: 1rem 0;
      gap: 0.5rem;
    }
  }
`;

const ADMIN_EMAIL = 'kaleabteka@gmail.com';

// UPDATE StripeButton for mobile and placement in avatar section
const StripeButton = styled.button`
  background: ${props => {
    if (props.loading) return 'rgba(99, 91, 255, 0.5)';
    if (props.active) return 'linear-gradient(135deg, #0b1bf3ff, #0b1bf3ff)';
    if (props.pending) return 'linear-gradient(135deg, #FFC107, #FFD54F)';
    return 'linear-gradient(135deg, #635BFF, #4F46E5)';
  }};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: ${props => props.loading ? 'wait' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 4px 12px rgba(99, 91, 255, 0.3);
  width: 100%;
  max-width: 280px;
  justify-content: center;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${props => 
      props.active ? 'rgba(0, 200, 83, 0.4)' : 
      props.pending ? 'rgba(255, 193, 7, 0.4)' :
      'rgba(99, 91, 255, 0.4)'
    };
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: white;
    animation: ${props => props.loading ? 'pulse 2s infinite' : 'none'};
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
    max-width: 240px;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const MarketplaceStats = styled.div`
  h3 {
    margin-bottom: 1rem;
    font-size: 1.1rem;
    color: #800000;
  }
  
  .stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
    
    .stat-item {
      text-align: center;
      padding: 0.75rem;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      
      .stat-number {
        font-size: 1.5rem;
        font-weight: bold;
        color: #800000;
        display: block;
      }
      
      .stat-label {
        font-size: 0.8rem;
        opacity: 0.8;
        margin-top: 0.25rem;
      }
    }
  }
  
  .rating-section {
    text-align: center;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    
    .rating-stars {
      display: flex;
      justify-content: center;
      gap: 0.25rem;
      margin-bottom: 0.5rem;
    }
    
    .rating-text {
      font-size: 0.9rem;
      opacity: 0.8;
    }
  }
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: ${props => props.columns || '1fr 1fr'};
  }
  
  &.single-column {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  label {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  input, textarea, select {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(128, 0, 0, 0.5);
    border-radius: 8px;
    padding: 0.75rem;
    color: white;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #800000;
      box-shadow: 0 0 0 2px rgba(128, 0, 0, 0.2);
    }
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }
  
  textarea {
    min-height: 80px;
    resize: vertical;
  }
  
  .field-description {
    font-size: 0.8rem;
    opacity: 0.7;
    margin-top: 0.25rem;
  }
`;

const DangerZoneCard = styled(ProfileCard)`
  border-color: rgba(244, 67, 54, 0.3);
  
  &:hover {
    border-color: rgba(244, 67, 54, 0.5);
  }
  
  .card-header {
    color: #F44336;
    
    h2 {
      color: #F44336;
    }
  }
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'danger' 
    ? 'rgba(244, 67, 54, 0.2)' 
    : 'rgba(255, 193, 7, 0.2)'};
  color: ${props => props.variant === 'danger' ? '#F44336' : '#FFC107'};
  border: 1px solid ${props => props.variant === 'danger' 
    ? 'rgba(244, 67, 54, 0.3)' 
    : 'rgba(255, 193, 7, 0.3)'};
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.variant === 'danger' 
      ? 'rgba(244, 67, 54, 0.3)' 
      : 'rgba(255, 193, 7, 0.3)'};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: #1a1a2e;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  border: 1px solid ${props => props.danger ? 'rgba(244, 67, 54, 0.3)' : 'rgba(255, 193, 7, 0.3)'};
  
  h3 {
    color: ${props => props.danger ? '#F44336' : '#FFC107'};
    margin: 0 0 1rem 0;
    font-size: 1.5rem;
  }
  
  p {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
  
  .modal-actions {
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;
  }
  
  .modal-button {
    flex: 1;
    padding: 0.75rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;
    
    &.confirm {
      background: ${props => props.danger ? '#F44336' : '#FFC107'};
      color: white;
      
      &:hover {
        opacity: 0.9;
      }
    }
    
    &.cancel {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      
      &:hover {
        background: rgba(255, 255, 255, 0.15);
      }
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const SkillsSection = styled.div`
  .skills-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
  
  .skill-tag {
    background: rgba(128, 0, 0, 0.2);
    color: #800000;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.9rem;
    border: 1px solid rgba(128, 0, 0, 0.3);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .remove-skill {
      background: none;
      border: none;
      color: inherit;
      cursor: pointer;
      padding: 0;
      display: flex;
      align-items: center;
      
      &:hover {
        opacity: 0.7;
      }
    }
  }
  
  .add-skill {
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;
    
    input {
      flex: 1;
    }
    
    button {
      background: rgba(128, 0, 0, 0.3);
      color: #800000;
      border: 1px solid rgba(128, 0, 0, 0.5);
      padding: 0.75rem;
      border-radius: 8px;
      cursor: pointer;
      
      &:hover {
        background: rgba(128, 0, 0, 0.5);
        color: white;
      }
    }
  }
`;

const ExperienceSection = styled.div`
  .experience-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1rem;
  }
  
  .experience-item {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    padding: 1rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    
    .experience-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 0.5rem;
      
      .experience-info {
        flex: 1;
        
        .experience-title {
          font-weight: bold;
          color: #800000;
        }
        
        .experience-company {
          font-size: 0.9rem;
          opacity: 0.8;
        }
        
        .experience-duration {
          font-size: 0.8rem;
          opacity: 0.7;
          margin-top: 0.25rem;
        }
      }
      
      .remove-experience {
        background: none;
        border: none;
        color: rgba(255, 255, 255, 0.5);
        cursor: pointer;
        padding: 0.25rem;
        
        &:hover {
          color: #ff4444;
        }
      }
    }
    
    .experience-description {
      font-size: 0.9rem;
      opacity: 0.8;
      line-height: 1.4;
    }
  }
  
  .add-experience {
    background: rgba(255, 255, 255, 0.05);
    border: 2px dashed rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 1rem;
    cursor: pointer;
    text-align: center;
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.3s ease;
    
    &:hover {
      border-color: rgba(128, 0, 0, 0.5);
      color: #800000;
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const StatusMessage = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &.success {
    background: rgba(76, 175, 80, 0.2);
    color: #4CAF50;
    border: 1px solid rgba(76, 175, 80, 0.3);
  }
  
  &.error {
    background: rgba(244, 67, 54, 0.2);
    color: #F44336;
    border: 1px solid rgba(244, 67, 54, 0.3);
  }
`;

const SELLER_TYPES = [
  'Product Seller',
  'Service Provider', 
  'Digital Creator',
  'Consultant',
  'Freelancer',
  'Artisan/Craftsperson',
  'Educator/Trainer',
  'Event Organizer',
  'Tech Developer',
  'Creative Professional'
];


// ADD this styled component for mobile bottom nav
const MobileBottomNav = styled.nav`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.95);
    backdrop-filter: blur(10px);
    border-top: 1px solid rgba(128, 0, 0, 0.3);
    padding: 0.75rem 0;
    z-index: 1000;
    justify-content: space-around;
    align-items: center;
  }
`;

const NavButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.active ? '#800000' : 'rgba(255, 255, 255, 0.6)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: all 0.3s;
  
  &:active {
    transform: scale(0.95);
  }
  
  .label {
    font-size: 0.7rem;
    font-weight: 500;
  }
  
  svg {
    width: 22px;
    height: 22px;
  }
`;

// UPDATE PageContainer to add bottom padding on mobile
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #0B0B3B, #1A1A4C);
  color: #FFFFFF;
  padding-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding-bottom: 80px; /* Space for bottom nav */
  }
`;

const ProfilePage = () => {
  const { user } = useAuth();
  const { accessibleShops, reloadShopAccess } = useActiveShop();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [stripeChecked, setStripeChecked] = useState(false);
  const [stripeProcessing, setStripeProcessing] = useState(false);
  const [showStripeWizard, setShowStripeWizard] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
const [showPauseModal, setShowPauseModal] = useState(false);
const [accountStatus, setAccountStatus] = useState('active'); // 'active', 'paused', or 'deleted'
const [deleteConfirmText, setDeleteConfirmText] = useState('');
const [processingAction, setProcessingAction] = useState(false);
const [showResetStripeModal, setShowResetStripeModal] = useState(false);
const [resetStripeConfirmText, setResetStripeConfirmText] = useState('');

  // NEW: Billing state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [billingDataForPayment, setBillingDataForPayment] = useState(null);

  const [profileData, setProfileData] = useState({
    displayName: '',
    professionalTitle: '',
    email: '',
    phone: '',
    location: '',
    website: '',
    bio: '',
    sellerType: '',
    skills: [],
    experience: [],
    avatar: null,
    hourlyRate: '',
    responseTime: '24 hours',
    languages: [],
    items: [], // NEW: Added for billing calculation
    services: [] // NEW: Added for billing calculation
  });
  
  const [originalData, setOriginalData] = useState({});
  const [userStats, setUserStats] = useState({
    itemsSold: 0,
    itemsBought: 0,
    totalEarnings: 0,
    rating: 0,
    reviewCount: 0,
    responseRate: 0,
    joinDate: null
  });
  
  const [newSkill, setNewSkill] = useState('');
  const [showAddExperience, setShowAddExperience] = useState(false);
  const [newExperience, setNewExperience] = useState({
    title: '',
    company: '',
    duration: '',
    description: ''
  });

  // [KEEP ALL YOUR EXISTING useEffects HERE]

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stripeParam = urlParams.get('stripe');
    
    if (stripeParam) {
      console.log('🔙 Returned from Stripe:', stripeParam);
      
      if (stripeParam === 'success') {
        setStatusMessage({ 
          type: 'success', 
          text: '⏳ Checking account status...' 
        });
        
        setTimeout(async () => {
          setStripeChecked(false);
          await manualCheckStripe();
        }, 2000);
        
      } else if (stripeParam === 'refresh') {
        setStatusMessage({ 
          type: 'warning', 
          text: '⚠️ Setup interrupted. Click Connect Stripe to continue.' 
        });
      }
      
      window.history.replaceState({}, '', '/profile');
    }
  }, []); 

  useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const paymentStatus = urlParams.get('payment');
  
  if (paymentStatus === 'success') {
    setStatusMessage({
      type: 'success',
      text: '✅ Payment processed successfully!'
    });
    
    // Clean URL
    window.history.replaceState({}, '', '/profile');
    
    // Refresh page after 2 seconds to show updated balance
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  }
}, []);

  useEffect(() => {
    const loadProfileData = async () => {
  if (!user) return;
  
  try {
    setLoading(true);
    
    // ✅ Load user data
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);

    let userData = {};
    if (userDoc.exists()) {
      userData = userDoc.data();
    }
    
    // ✅ NEW: Calculate total earnings from orders (similar to ShopPage logic)
    let totalEarned = 0;
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('sellerId', '==', user.uid),
        where('status', 'in', ['completed', 'picked_up'])
      );
      
      const ordersSnap = await getDocs(q);
      ordersSnap.forEach((docSnap) => {
        const order = docSnap.data();
        const orderTotal = order.total || 0;
        const platformFee = orderTotal * 0.05;
        const sellerEarnings = orderTotal - platformFee;
        totalEarned += sellerEarnings;
      });
      
      console.log('💰 Calculated total earnings for profile:', totalEarned);
      
      // Update user document with calculated earnings
      if (userData.totalEarnings !== totalEarned) {
        await updateDoc(userDocRef, {
          totalEarnings: totalEarned,
          updatedAt: serverTimestamp()
        });
        userData.totalEarnings = totalEarned;
      }
    } catch (error) {
      console.error('Error calculating earnings:', error);
    }
    
    // ✅ Load shop data for items/services AND profile picture
    const shopDocRef = doc(db, 'shops', user.uid);
    const shopDoc = await getDoc(shopDocRef);
        
        let shopItems = [];
        let shopServices = [];
        let shopProfilePic = null;
        
        if (shopDoc.exists()) {
          const shopData = shopDoc.data();
          shopItems = shopData.items || [];
          shopServices = shopData.services || [];
          shopProfilePic = shopData.profile || null; // ✅ Get profile from shop
          
          console.log('✅ Shop data loaded:', {
            hasProfile: !!shopProfilePic,
            profileUrl: shopProfilePic,
            itemsCount: shopItems.length,
            servicesCount: shopServices.length
          });
        }
        
        // ✅ FIXED: Use shop profile if available, fallback to user photoURL
        const profilePicture = shopProfilePic || user.photoURL || userData.avatar || null;
        
        console.log('📸 Profile picture priority:', {
          shopProfile: shopProfilePic,
          userPhotoURL: user.photoURL,
          userDataAvatar: userData.avatar,
          finalChoice: profilePicture
        });
        
        const initialData = {
          displayName: user.displayName || userData.displayName || '',
          professionalTitle: userData.professionalTitle || '',
          email: user.email || '',
          phone: userData.phone || '',
          location: userData.location || '',
          website: userData.website || '',
          bio: userData.bio || '',
          sellerType: userData.sellerType || '',
          skills: userData.skills || [],
          experience: userData.experience || [],
          avatar: profilePicture, // ✅ Use shop profile
          hourlyRate: userData.hourlyRate || '',
          responseTime: userData.responseTime || '24 hours',
          languages: userData.languages || [],
          items: shopItems,
          services: shopServices
        };
        
        setProfileData(initialData);
        setOriginalData(initialData);
        
        setUserStats({
          itemsSold: userData.itemsSold || 0,
          itemsBought: userData.itemsBought || 0,
          totalEarnings: userData.totalEarnings || 0,
          rating: userData.rating || 0,
          reviewCount: userData.reviewCount || 0,
          responseRate: userData.responseRate || 0,
          joinDate: user.metadata?.creationTime || Date.now()
        });
        
      } catch (error) {
        console.error('Error loading profile:', error);
        setStatusMessage({ type: 'error', text: 'Failed to load profile data' });
      } finally {
        setLoading(false);
      }
    };
    
    loadProfileData();
  }, [user]);

  useEffect(() => {
  const loadAccountStatus = async () => {
    if (!user) return;
    
    try {
      const shopRef = doc(db, 'shops', user.uid);
      const shopSnap = await getDoc(shopRef);
      
      if (shopSnap.exists()) {
        const data = shopSnap.data();
        setAccountStatus(data.accountStatus || 'active');
      }
    } catch (error) {
      console.error('Error loading account status:', error);
    }
  };
  
  loadAccountStatus();
}, [user]);


// Replace the handlePauseAccount function:
const handlePauseAccount = async () => {
  if (processingAction) return;
  
  try {
    setProcessingAction(true);
    
    const newStatus = accountStatus === 'paused' ? 'active' : 'paused';
    const requestType = newStatus === 'paused' ? 'pause_request' : 'resume_request';
    
    // Create request in Firestore instead of direct action
    const requestRef = collection(db, 'accountRequests');
    await addDoc(requestRef, {
      userId: user.uid,
      shopName: profileData.displayName,
      username: profileData.username || 'N/A',
      requestType,
      currentStatus: accountStatus,
      requestedStatus: newStatus,
      createdAt: serverTimestamp(),
      status: 'pending', // pending, approved, rejected
      processedBy: null,
      processedAt: null
    });
    
    setShowPauseModal(false);
    
    setStatusMessage({
      type: 'success',
      text: newStatus === 'paused' 
        ? '📩 Pause request submitted. Admin will review shortly.'
        : '📩 Resume request submitted. Admin will review shortly.'
    });
    
  } catch (error) {
    console.error('Error submitting request:', error);
    setStatusMessage({
      type: 'error',
      text: 'Failed to submit request. Please try again.'
    });
  } finally {
    setProcessingAction(false);
  }
};

// Replace handleDeleteAccount function
const handleDeleteAccount = async () => {
  if (processingAction) return;
  
  if (deleteConfirmText !== 'DELETE') {
    setStatusMessage({
      type: 'error',
      text: 'Please type DELETE to confirm'
    });
    return;
  }
  
  try {
    setProcessingAction(true);
    
    // Create deletion request
    const requestRef = collection(db, 'accountRequests');
    await addDoc(requestRef, {
      userId: user.uid,
      shopName: profileData.displayName,
      username: profileData.username || 'N/A',
      requestType: 'delete_request',
      currentStatus: accountStatus,
      confirmationText: deleteConfirmText,
      createdAt: serverTimestamp(),
      status: 'pending',
      processedBy: null,
      processedAt: null
    });
    
    setShowDeleteModal(false);
    setDeleteConfirmText('');
    
    setStatusMessage({
      type: 'success',
      text: '📩 Deletion request submitted. Admin will review shortly. You will receive a notification when processed.'
    });
    
  } catch (error) {
    console.error('Error submitting deletion request:', error);
    setStatusMessage({
      type: 'error',
      text: error.message || 'Failed to submit request. Please contact support.'
    });
  } finally {
    setProcessingAction(false);
  }
};

const handleResetStripeProgress = async () => {
  if (resetStripeConfirmText !== 'RESET') {
    setStatusMessage({
      type: 'error',
      text: 'Please type RESET to confirm'
    });
    return;
  }
  
  try {
    setProcessingAction(true);
    
    console.log('🔄 Resetting Stripe progress for user:', user.uid);
    
    // Clear local state
    setStripeStatus(null);
    setStripeChecked(false);
    
    // Clear Firestore shop data
    const shopRef = doc(db, 'shops', user.uid);
    await updateDoc(shopRef, {
      stripeAccountId: null,
      stripeAccountStatus: 'none',
      stripeReady: false,
      chargesEnabled: false,
      payoutsEnabled: false,
      detailsSubmitted: false,
      stripeMode: null,
      onboardingInitiated: false,
      onboardingInitiatedAt: null,
      payoutControl: null,
      payoutScheduleUpdated: null,
      currentlyDue: [],
      errors: [],
      lastChecked: null,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Stripe progress reset successfully');
    
    setShowResetStripeModal(false);
    setResetStripeConfirmText('');
    
    setStatusMessage({
      type: 'success',
      text: '✅ Stripe setup progress has been reset. You can start fresh now!'
    });
    
    // Force re-check after reset
    setTimeout(() => {
      setStripeChecked(false);
    }, 1000);
    
  } catch (error) {
    console.error('❌ Error resetting Stripe progress:', error);
    setStatusMessage({
      type: 'error',
      text: 'Failed to reset Stripe progress: ' + error.message
    });
  } finally {
    setProcessingAction(false);
  }
};

  // ✅ UPDATED: Avatar upload should update BOTH shops.profile AND users.avatar
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      setStatusMessage({ type: 'error', text: 'Image must be less than 5MB' });
      return;
    }
    
    try {
      setSaving(true);
      
      const avatarRef = ref(storage, `avatars/${user.uid}/${Date.now()}`);
      const snapshot = await uploadBytes(avatarRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // ✅ Update Firebase Auth profile
      await updateProfile(auth.currentUser, {
        photoURL: downloadURL
      });
      
      // ✅ Update shop profile (primary location)
      const shopRef = doc(db, 'shops', user.uid);
      await updateDoc(shopRef, {
        profile: downloadURL,
        updatedAt: serverTimestamp()
      });
      
      // ✅ Also update users collection for backwards compatibility
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        avatar: downloadURL,
        profilePic: downloadURL, // Some code uses this field
        updatedAt: serverTimestamp()
      });
      
      handleInputChange('avatar', downloadURL);
      setStatusMessage({ type: 'success', text: 'Profile photo updated!' });
      
      console.log('✅ Avatar updated in:', {
        authProfile: true,
        shopProfile: true,
        userProfile: true,
        url: downloadURL
      });
      
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setStatusMessage({ type: 'error', text: 'Failed to upload profile photo' });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const hasProfileChanges = JSON.stringify(profileData) !== JSON.stringify(originalData);
    setHasChanges(hasProfileChanges);
  }, [profileData, originalData]);

  useEffect(() => {
    const checkStripeOnce = async () => {
      if (!user || stripeChecked) return;
      
      try {
        setStripeLoading(true);
        console.log('🔍 Initial Stripe check for user:', user.uid);
        
        const shopRef = doc(db, 'shops', user.uid);
        const shopSnap = await getDoc(shopRef);
        
        if (shopSnap.exists()) {
          const data = shopSnap.data();
          setStripeStatus({
            accountId: data.stripeAccountId,
            status: data.stripeAccountStatus,
            chargesEnabled: data.chargesEnabled,
            payoutsEnabled: data.payoutsEnabled,
            stripeReady: data.stripeReady
          });
          console.log('✅ Stripe status from Firestore:', data.stripeAccountStatus);
        } else {
          console.log('📦 No shop document - Stripe not set up');
          setStripeStatus(null);
        }
        
        setStripeChecked(true);
      } catch (error) {
        console.error('❌ Error checking Stripe:', error);
        setStripeStatus(null);
        setStripeChecked(true);
      } finally {
        setStripeLoading(false);
      }
    };
    
    checkStripeOnce();
  }, [user, stripeChecked]);

  const manualCheckStripe = async () => {
    if (!user) return;
    
    try {
      setStripeLoading(true);
      console.log('🔄 Manual Stripe status check');
      
      const response = await fetch('/api/check-connect-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ API check result:', result);
        
        if (result.found) {
          setStripeStatus({
            accountId: result.accountId,
            status: result.status,
            chargesEnabled: result.chargesEnabled,
            payoutsEnabled: result.payoutsEnabled,
            stripeReady: result.stripeReady
          });
          
          if (result.status === 'active') {
            setStatusMessage({ 
              type: 'success', 
              text: '✅ Stripe account is active!' 
            });
          } else if (result.status === 'pending_verification') {
            setStatusMessage({ 
              type: 'warning', 
              text: '⏳ Account under review (1-2 business days)' 
            });
          }
        } else {
          setStripeStatus(null);
          setStatusMessage({ 
            type: 'warning', 
            text: '⚠️ No Stripe account found' 
          });
        }
      }
    } catch (error) {
      console.error('❌ Manual check error:', error);
      setStatusMessage({ 
        type: 'error', 
        text: 'Failed to check status' 
      });
    } finally {
      setStripeLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    if (!stripeStatus?.accountId) {
      console.error('No account ID available');
      return;
    }
    
    try {
      console.log('🔗 Opening dashboard for:', stripeStatus.accountId);
      
      const response = await fetch('/api/create-dashboard-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: stripeStatus.accountId })
      });

      const data = await response.json();
      if (response.ok && data.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error(data.error || 'Failed to create dashboard link');
      }
    } catch (error) {
      console.error('❌ Dashboard error:', error);
      setStatusMessage({
        type: 'error',
        text: `Could not open Stripe dashboard: ${error.message}`
      });
    }
  };

  const handleStripeClick = () => {
    if (stripeStatus?.status === 'active' && stripeStatus?.stripeReady) {
      openStripeDashboard();
    } else {
      console.log('🚀 Opening Stripe wizard');
      setShowStripeWizard(true);
    }
  };

  const getStripeButtonText = () => {
    if (stripeLoading) return 'Checking...';
    
    const status = stripeStatus?.status;
    const isReady = stripeStatus?.stripeReady;
    
    if (status === 'active' && isReady) return '✅ Stripe Dashboard';
    if (status === 'pending_verification') return '⏳ Complete Verification';
    if (status === 'incomplete') return '⚠️ Resume Setup';
    if (stripeStatus?.accountId) return 'Continue Stripe Setup';
    
    return 'Connect Stripe Account';
  };

  // NEW: Billing handlers
  const handleOpenPayment = (billingData) => {
    console.log('💳 Opening payment modal with data:', billingData);
    setBillingDataForPayment({
      ...billingData,
      userId: user.uid
    });
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    console.log('✅ Payment successful, refreshing page...');
    setShowPaymentModal(false);
    setBillingDataForPayment(null);
    setStatusMessage({
      type: 'success',
      text: '✅ Payment processed successfully!'
    });
    // Refresh to update balance
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };



  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !profileData.skills.includes(newSkill.trim())) {
      handleInputChange('skills', [...profileData.skills, newSkill.trim()]);
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    handleInputChange('skills', profileData.skills.filter(skill => skill !== skillToRemove));
  };

  const handleAddExperience = () => {
    if (newExperience.title.trim() && newExperience.company.trim()) {
      handleInputChange('experience', [...profileData.experience, { ...newExperience, id: Date.now() }]);
      setNewExperience({ title: '', company: '', duration: '', description: '' });
      setShowAddExperience(false);
    }
  };

  const handleRemoveExperience = (experienceId) => {
    handleInputChange('experience', profileData.experience.filter(exp => exp.id !== experienceId));
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      
      const userDocRef = doc(db, 'users', user.uid);
      
      await updateDoc(userDocRef, {
        ...profileData,
        updatedAt: new Date().toISOString()
      });
      
      if (profileData.displayName !== user.displayName) {
        await updateProfile(auth.currentUser, {
          displayName: profileData.displayName
        });
      }
      
      setOriginalData({ ...profileData });
      setHasChanges(false);
      
      setStatusMessage({ type: 'success', text: 'Profile updated successfully!' });
      
      setTimeout(() => setStatusMessage(null), 3000);
      
    } catch (error) {
      console.error('Error saving profile:', error);
      setStatusMessage({ type: 'error', text: 'Failed to save profile changes' });
    } finally {
      setSaving(false);
    }
  };

  const renderStarRating = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        size={16} 
        fill={i < rating ? '#FFD700' : 'none'} 
        color={i < rating ? '#FFD700' : 'rgba(255,255,255,0.3)'} 
      />
    ));
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <LoadingSpinner />
          <p>Loading profile...</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <ProfileHeader>
        <HeaderContent>
          <h1>
            <User size={14} />
            Profile
          </h1>
          <HeaderActions>
            <SaveButton 
              hasChanges={hasChanges} 
              onClick={handleSaveProfile}
              disabled={!hasChanges || saving}
            >
              {saving ? <LoadingSpinner /> : <Save size={16} />}
              <span className="save-text">Save</span>
            </SaveButton>
          </HeaderActions>
        </HeaderContent>
      </ProfileHeader>

      <ProfileContent>
        
        {statusMessage && (
          <StatusMessage className={statusMessage.type}>
            {statusMessage.text}
            <button 
              onClick={() => setStatusMessage(null)}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'inherit', 
                marginLeft: 'auto',
                cursor: 'pointer'
              }}
            >
              ×
            </button>
          </StatusMessage>
        )}

        {stripeStatus?.accountId && !stripeStatus?.stripeReady && (
          <div style={{
            background: 'rgba(255, 193, 7, 0.1)',
            border: '1px solid rgba(255, 193, 7, 0.3)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            color: '#FFC107'
          }}>
            <strong>⚠️ Stripe Setup Required</strong>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
              Your Stripe account needs additional information. Click the Stripe button below to complete setup.
            </p>
          </div>
        )}

        <ProfileGrid>
          <ProfileSidebar>
        <ProfileCard>
          <AvatarSection>
  <div className="avatar-container">
  <div className="avatar">
    {profileData.avatar ? (
      <>
        <img 
          src={profileData.avatar} 
          alt="Profile"
          onError={(e) => {
            console.error('❌ Avatar failed to load:', profileData.avatar);
            // Hide the image
            e.target.style.display = 'none';
            // Show the fallback icon
            const fallback = e.target.parentElement?.querySelector('.avatar-fallback');
            if (fallback) {
              fallback.style.display = 'flex';
            }
          }}
          style={{ display: 'block' }}
        />
        <div 
          className="avatar-fallback" 
          style={{ 
            display: 'none',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <User size={60} />
        </div>
      </>
    ) : (
      <div 
        className="avatar-fallback" 
        style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%'
        }}
      >
        <User size={60} />
      </div>
    )}
  </div>
  <div className="avatar-upload">
    <input
      type="file"
      accept="image/*"
      onChange={handleAvatarUpload}
      id="avatar-upload"
    />
    <label htmlFor="avatar-upload" style={{ cursor: 'pointer', display: 'flex' }}>
      <Camera size={18} />
    </label>
  </div>
</div>
                
                <div className="display-name">
                  {profileData.displayName || 'Your Name'}
                </div>
                <div className="professional-title">
                  {profileData.professionalTitle || ''}
                </div>
                

{/* NEW: Account Balance Button */}
<AccountBalanceButton
  userId={user?.uid}
  shopData={{
    items: profileData.items || [],
    services: profileData.services || []
  }}
  onOpenPayment={handleOpenPayment}
  theme={{ colors: { accent: '#800000', text: '#FFFFFF' } }}
/>


              </AvatarSection>
            </ProfileCard>

            {user?.email?.toLowerCase() === ADMIN_EMAIL && (
  <button
    onClick={() => navigate('/admin/dashboard')}
    style={{
      position: 'absolute',
      top: '1rem',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'linear-gradient(45deg, #800000, #4A0404)',
      color: 'white',
      border: 'none',
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontWeight: '600',
      fontSize: '0.9rem',
      zIndex: 20,
      boxShadow: '0 4px 12px rgba(128, 0, 0, 0.4)',
      transition: 'all 0.3s ease'
    }}
    onMouseOver={(e) => {
      e.currentTarget.style.transform = 'translateX(-50%) translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 6px 16px rgba(128, 0, 0, 0.6)';
    }}
    onMouseOut={(e) => {
      e.currentTarget.style.transform = 'translateX(-50%)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(128, 0, 0, 0.4)';
    }}
  >
    <BarChart2 size={18} />
    Admin Dashboard
  </button>
)}

            {/* Marketplace Stats */}
            <ProfileCard>
              <MarketplaceStats>
                <h3>Marketplace Performance</h3>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-number">{userStats.itemsSold}</span>
                    <span className="stat-label">Items Sold</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{userStats.itemsBought}</span>
                    <span className="stat-label">Items Bought</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">${userStats.totalEarnings.toFixed(2)}</span>
                    <span className="stat-label">Total Earnings</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{userStats.responseRate}%</span>
                    <span className="stat-label">Response Rate</span>
                  </div>
                </div>
                
                <div className="rating-section">
                  <div className="rating-stars">
                    {renderStarRating(userStats.rating)}
                  </div>
                  <div className="rating-text">
                    {userStats.rating.toFixed(1)} out of 5 ({userStats.reviewCount} reviews)
                  </div>
                </div>
              </MarketplaceStats>
            </ProfileCard>
          </ProfileSidebar>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Personal Information */}
            <ProfileCard>
              <div className="card-header">
                <User size={20} />
                <h2>Personal Information</h2>
              </div>

              <FormGrid>
                <FormField>
                  <label>
                    <User size={14} />
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={profileData.displayName}
                    onChange={(e) => handleInputChange('displayName', e.target.value)}
                    placeholder="Enter your full name"
                  />
                </FormField>

                <FormField>
                  <label>
                    <Mail size={14} />
                    Email
                  </label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="your@email.com"
                  />
                </FormField>

                <FormField>
                  <label>
                    <Phone size={14} />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                </FormField>

                <FormField>
                  <label>
                    <MapPin size={14} />
                    Location
                  </label>
                  <input
                    type="text"
                    value={profileData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="City, State/Country"
                  />
                </FormField>

                <FormField className="single-column">
                  <label>
                    <Globe size={14} />
                    Website/Portfolio
                  </label>
                  <input
                    type="url"
                    value={profileData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://yourwebsite.com"
                  />
                </FormField>

                <FormField className="single-column">
                  <label>Professional Bio</label>
                  <textarea
                    value={profileData.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    placeholder="Tell others about your background, expertise, and what makes you unique..."
                    maxLength={500}
                  />
                  <div className="field-description">
                    {profileData.bio.length}/500 characters
                  </div>
                </FormField>
              </FormGrid>
            </ProfileCard>

            {/* Professional Details */}
            <ProfileCard>
              <div className="card-header">
                <Briefcase size={20} />
                <h2>Professional Details</h2>
              </div>

              <FormGrid>
                <FormField>
                  <label>
                    <Award size={14} />
                    Professional Title
                  </label>
                  <input
                    type="text"
                    value={profileData.professionalTitle}
                    onChange={(e) => handleInputChange('professionalTitle', e.target.value)}
                    placeholder="e.g., Senior Software Developer"
                  />
                </FormField>

                <FormField>
                  <label>
                    <DollarSign size={14} />
                    Hourly Rate
                  </label>
                  <input
                    type="text"
                    value={profileData.hourlyRate}
                    onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
                    placeholder="$50/hour"
                  />
                </FormField>

                <FormField>
                  <label>
                    <Target size={14} />
                    Seller Type
                  </label>
                  <select
                    value={profileData.sellerType}
                    onChange={(e) => handleInputChange('sellerType', e.target.value)}
                  >
                    <option value="">Select seller type</option>
                    {SELLER_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </FormField>

                <FormField>
                  <label>
                    <Globe size={14} />
                    Languages
                  </label>
                  <input
                    type="text"
                    value={profileData.languages.join(', ')}
                    onChange={(e) => handleInputChange('languages', e.target.value.split(',').map(lang => lang.trim()))}
                    placeholder="English, Spanish, French..."
                  />
                </FormField>
              </FormGrid>

              {/* Skills Section */}
              <div style={{ marginTop: '1.5rem' }}>
                <h3 style={{ marginBottom: '1rem', color: '#800000', fontSize: '1.1rem' }}>Skills & Expertise</h3>
                <SkillsSection>
                  <div className="skills-list">
                    {profileData.skills.map((skill, index) => (
                      <div key={index} className="skill-tag">
                        {skill}
                        <button 
                          className="remove-skill"
                          onClick={() => handleRemoveSkill(skill)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="add-skill">
                    <input
                      type="text"
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      placeholder="Add a skill..."
                      onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                    />
                    <button onClick={handleAddSkill}>
                      <Plus size={16} />
                    </button>
                  </div>
                </SkillsSection>
              </div>
            </ProfileCard>

            {/* Work Experience */}
            <ProfileCard>
              <div className="card-header">
                <GraduationCap size={20} />
                <h2>Work Experience</h2>
              </div>

              <ExperienceSection>
                <div className="experience-list">
                  {profileData.experience.map((exp, index) => (
                    <div key={exp.id || index} className="experience-item">
                      <div className="experience-header">
                        <div className="experience-info">
                          <div className="experience-title">{exp.title}</div>
                          <div className="experience-company">{exp.company}</div>
                          <div className="experience-duration">{exp.duration}</div>
                        </div>
                        <button 
                          className="remove-experience"
                          onClick={() => handleRemoveExperience(exp.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      {exp.description && (
                        <div className="experience-description">{exp.description}</div>
                      )}
                    </div>
                  ))}
                </div>

                {!showAddExperience && (
                  <div 
                    className="add-experience"
                    onClick={() => setShowAddExperience(true)}
                  >
                    <Plus size={20} />
                    <div>Add Work Experience</div>
                  </div>
                )}

                {showAddExperience && (
                  <div style={{ 
                    background: 'rgba(255, 255, 255, 0.05)', 
                    padding: '1rem', 
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <FormGrid>
                      <FormField>
                        <label>Job Title</label>
                        <input
                          type="text"
                          value={newExperience.title}
                          onChange={(e) => setNewExperience(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="e.g., Senior Developer"
                        />
                      </FormField>
                      <FormField>
                        <label>Company</label>
                        <input
                          type="text"
                          value={newExperience.company}
                          onChange={(e) => setNewExperience(prev => ({ ...prev, company: e.target.value }))}
                          placeholder="e.g., TechCorp Inc."
                        />
                      </FormField>
                      <FormField>
                        <label>Duration</label>
                        <input
                          type="text"
                          value={newExperience.duration}
                          onChange={(e) => setNewExperience(prev => ({ ...prev, duration: e.target.value }))}
                          placeholder="e.g., Jan 2020 - Present"
                        />
                      </FormField>
                      <FormField className="single-column">
                        <label>Description</label>
                        <textarea
                          value={newExperience.description}
                          onChange={(e) => setNewExperience(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Describe your role and achievements..."
                        />
                      </FormField>
                    </FormGrid>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                      <button
                        onClick={handleAddExperience}
                        style={{
                          flex: 1,
                          background: 'rgba(128, 0, 0, 0.3)',
                          color: '#800000',
                          border: '1px solid rgba(128, 0, 0, 0.5)',
                          padding: '0.75rem 1rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: 600
                        }}
                      >
                        Add Experience
                      </button>
                      <button
                        onClick={() => {
                          setShowAddExperience(false);
                          setNewExperience({ title: '', company: '', duration: '', description: '' });
                        }}
                        style={{
                          flex: 1,
                          background: 'transparent',
                          color: 'rgba(255, 255, 255, 0.7)',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          padding: '0.75rem 1rem',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </ExperienceSection>
              
            </ProfileCard>
          </div>
        </ProfileGrid>


      </ProfileContent>

      {/* Stripe Wizard */}
      {showStripeWizard && (
        <StripeWizard
          onClose={() => setShowStripeWizard(false)}
          onComplete={async () => {
            console.log('✅ Wizard completed');
            setShowStripeWizard(false);
            setStripeChecked(false);
            await manualCheckStripe();
            setStatusMessage({ 
              type: 'success', 
              text: '✅ Stripe setup complete!' 
            });
          }}
          userId={user.uid}
          userEmail={user.email}
          shopName={profileData.displayName}
          existingAccountData={stripeStatus}
        />
      )}

      {/* NEW: Payment Modal */}
      {showPaymentModal && billingDataForPayment && (
        <PaymentModal
          billingData={billingDataForPayment}
          onClose={() => {
            setShowPaymentModal(false);
            setBillingDataForPayment(null);
          }}
          onSuccess={handlePaymentSuccess}
          theme={{ colors: { accent: '#800000', text: '#FFFFFF', surface: '#1a1a1a' } }}
        />
      )}

      {/* Mobile Bottom Nav */}
      {user && (
        <MobileBottomNav>
          <NavButton onClick={() => navigate('/welcome')}>
            <Home size={22} />
            <span className="label">Home</span>
          </NavButton>
          
          <NavButton onClick={() => navigate('/explore')}>
            <Search size={22} />
            <span className="label">Explore</span>
          </NavButton>
          
          <NavButton onClick={() => navigate('/messages')}>
            <MessageCircle size={22} />
            <span className="label">Messages</span>
          </NavButton>
          
          <NavButton onClick={() => navigate('/shop/manage')}>
            <Store size={22} />
            <span className="label">My Shop</span>
          </NavButton>
          
          <NavButton active onClick={() => navigate('/profile')}>
            <User size={22} />
            <span className="label">Profile</span>
          </NavButton>
        </MobileBottomNav>
      )}

      <DangerZoneCard>
  <div className="card-header">
    <AlertCircle size={20} />
    <h2>Danger Zone</h2>
  </div>

  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
    <div>
      <h4 style={{ margin: '0 0 0.5rem 0', color: '#FFC107' }}>
        {accountStatus === 'paused' ? 'Resume Account' : 'Pause Account'}
      </h4>
      <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', opacity: 0.8 }}>
        {accountStatus === 'paused' 
          ? 'Make your items visible again in the marketplace.'
          : 'Temporarily hide all your items from the marketplace. You can resume anytime.'}
      </p>
      <ActionButton 
        variant="warning"
        onClick={() => setShowPauseModal(true)}
        disabled={processingAction}
      >
        <Clock size={16} />
        {accountStatus === 'paused' ? 'Resume Account' : 'Pause Account'}
      </ActionButton>
    </div>

    <div style={{ 
      borderTop: '1px solid rgba(244, 67, 54, 0.2)', 
      paddingTop: '1rem',
      marginTop: '0.5rem'
    }}>
      <h4 style={{ margin: '0 0 0.5rem 0', color: '#F44336' }}>Delete Account</h4>
      <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', opacity: 0.8 }}>
        Permanently delete your account and all associated data. This action cannot be undone.
      </p>
      <ActionButton 
        variant="danger"
        onClick={() => setShowDeleteModal(true)}
        disabled={processingAction}
      >
        <Trash2 size={16} />
        Delete Account
      </ActionButton>
    </div>
  </div>
</DangerZoneCard>

{/* Add these modals before the closing PageContainer tag */}
{showPauseModal && (
  <ModalOverlay onClick={() => setShowPauseModal(false)}>
    <ModalContent onClick={(e) => e.stopPropagation()}>
      <h3>{accountStatus === 'paused' ? 'Resume Account?' : 'Pause Account?'}</h3>
      <p>
        {accountStatus === 'paused' 
          ? 'Your items will become visible again in the marketplace and users will be able to place orders.'
          : 'Your items will be hidden from the marketplace. Users will not be able to see or order your items. You can resume your account anytime.'}
      </p>
      <div className="modal-actions">
        <button 
          className="modal-button cancel"
          onClick={() => setShowPauseModal(false)}
          disabled={processingAction}
        >
          Cancel
        </button>
        <button 
          className="modal-button confirm"
          onClick={handlePauseAccount}
          disabled={processingAction}
        >
          {processingAction ? (
            <LoadingSpinner />
          ) : (
            accountStatus === 'paused' ? 'Resume Account' : 'Pause Account'
          )}
        </button>
      </div>
    </ModalContent>
  </ModalOverlay>
)}

{showDeleteModal && (
  <ModalOverlay onClick={() => setShowDeleteModal(false)}>
    <ModalContent danger onClick={(e) => e.stopPropagation()}>
      <h3>Delete Account?</h3>
      <p>
        This will permanently delete your account and all associated data including:
      </p>
      <ul style={{ 
        color: 'rgba(255, 255, 255, 0.8)', 
        marginBottom: '1rem',
        paddingLeft: '1.5rem'
      }}>
        <li>All items and services</li>
        <li>Order history</li>
        <li>Messages and conversations</li>
        <li>Profile information</li>
        <li>Stripe account connection</li>
      </ul>
      <p style={{ color: '#F44336', fontWeight: 600 }}>
        This action cannot be undone!
      </p>
      
      <FormField>
        <label style={{ color: '#F44336' }}>
          Type "DELETE" to confirm
        </label>
        <input
          type="text"
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          placeholder="Type DELETE"
          style={{ 
            borderColor: deleteConfirmText === 'DELETE' ? '#F44336' : 'rgba(244, 67, 54, 0.3)'
          }}
        />
      </FormField>

      <div className="modal-actions">
        <button 
          className="modal-button cancel"
          onClick={() => {
            setShowDeleteModal(false);
            setDeleteConfirmText('');
          }}
          disabled={processingAction}
        >
          Cancel
        </button>
        <button 
          className="modal-button confirm"
          onClick={handleDeleteAccount}
          disabled={processingAction || deleteConfirmText !== 'DELETE'}
        >
          {processingAction ? (
            <LoadingSpinner />
          ) : (
            'Delete Forever'
          )}
        </button>
      </div>
    </ModalContent>
  </ModalOverlay>
)}

{showResetStripeModal && (
  <ModalOverlay onClick={() => setShowResetStripeModal(false)}>
    <ModalContent danger onClick={(e) => e.stopPropagation()}>
      <h3>Reset Stripe Setup?</h3>
      <p>
        This will clear all your Stripe setup progress including:
      </p>
      <ul style={{ 
        color: 'rgba(255, 255, 255, 0.8)', 
        marginBottom: '1rem',
        paddingLeft: '1.5rem'
      }}>
        <li>Account connection history</li>
        <li>Onboarding progress</li>
        <li>Verification status</li>
        <li>All setup attempts</li>
      </ul>
      <p style={{ color: '#FFC107', fontWeight: 600 }}>
        ⚠️ Note: This only clears your setup progress in KalKode. 
        If you created a Stripe account, it still exists in Stripe.
      </p>
      <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', marginTop: '1rem' }}>
        Use this if you want to start the Stripe setup from scratch or 
        if you're experiencing issues with a stuck setup process.
      </p>
      
      <FormField>
        <label style={{ color: '#F44336' }}>
          Type "RESET" to confirm
        </label>
        <input
          type="text"
          value={resetStripeConfirmText}
          onChange={(e) => setResetStripeConfirmText(e.target.value)}
          placeholder="Type RESET"
          style={{ 
            borderColor: resetStripeConfirmText === 'RESET' ? '#F44336' : 'rgba(244, 67, 54, 0.3)'
          }}
        />
      </FormField>

      <div className="modal-actions">
        <button 
          className="modal-button cancel"
          onClick={() => {
            setShowResetStripeModal(false);
            setResetStripeConfirmText('');
          }}
          disabled={processingAction}
        >
          Cancel
        </button>
        <button 
          className="modal-button confirm"
          onClick={handleResetStripeProgress}
          disabled={processingAction || resetStripeConfirmText !== 'RESET'}
        >
          {processingAction ? (
            <LoadingSpinner />
          ) : (
            'Reset Progress'
          )}
        </button>
      </div>
    </ModalContent>
  </ModalOverlay>
)}

    </PageContainer>
  );
};

export default ProfilePage;